import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TOPIC_ICONS, FALLBACK_TOPIC_ICON } from '@/features/topics/icons';
import { Skeleton } from '@/components/Skeleton';
import { api } from '@/api/endpoints';
import { useAuth } from '@/store/auth';
import { useUI } from '@/store/ui';
import type { Topic } from '@/api/types';

function TopicIcon({ id }: { id: string }) {
  const Cmp = TOPIC_ICONS[id] ?? FALLBACK_TOPIC_ICON;
  return <Cmp size={22} />;
}

export default function Topics() {
  const { t } = useTranslation();
  const user = useAuth(s => s.user);
  const patchUser = useAuth(s => s.patchUser);
  const showToast = useUI(s => s.showToast);

  const [all, setAll] = useState<Topic[] | null>(null);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    void api.topics.list().then(({ data }) => setAll(data));
  }, []);

  useEffect(() => {
    if (user?.topics) setSelected(user.topics);
  }, [user?.topics]);

  const status = useMemo(() => {
    const n = selected.length;
    if (n < 3) return { label: t('onboarding.topics_count_min', { n }), valid: false };
    if (n > 5) return { label: t('onboarding.topics_count_max'), valid: false };
    return { label: t('onboarding.topics_count_ok', { n }), valid: true };
  }, [selected, t]);

  const toggle = (id: string) => {
    setSelected(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  // Auto-save с дебаунсом: ждём 400 мс после последнего тапа, если набор валиден и реально изменился.
  const lastSaved = useRef<string | null>(null);
  useEffect(() => {
    // Игнорим первый прокидывающий-снаружи стейт.
    if (!user?.topics) return;
    const sig = [...selected].sort().join(',');
    const baseline = [...user.topics].sort().join(',');
    if (sig === baseline) { lastSaved.current = sig; return; }
    if (!status.valid) return;
    if (sig === lastSaved.current) return;
    const id = setTimeout(() => {
      api.me.setTopics(selected)
        .then(() => { lastSaved.current = sig; patchUser({ topics: selected }); })
        .catch(() => showToast(t('errors.generic'), 'error'));
    }, 400);
    return () => clearTimeout(id);
  }, [selected, status.valid, user?.topics, patchUser, showToast, t]);

  if (!all) return <div className="p-4 space-y-3"><Skeleton height={56} /><Skeleton height={56} /></div>;

  const active = all.filter(tp => selected.includes(tp.id));
  const available = all.filter(tp => !selected.includes(tp.id));

  return (
    <div className="flex-1 flex flex-col bg-bg min-h-0">
      <header className="px-4 pt-4 pb-2">
        <h1 className="text-xl font-medium">{t('profile.menu_topics')}</h1>
        <p className="text-xs text-text-muted mt-1">{status.label}</p>
      </header>

      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-5">
        {active.length > 0 && (
          <section>
            <h2 className="text-xs uppercase tracking-wider text-text-muted mb-2">{t('topics.active')}</h2>
            <div className="space-y-2">
              {active.map(tp => (
                <TopicRow key={tp.id} topic={tp} active onClick={() => toggle(tp.id)} />
              ))}
            </div>
          </section>
        )}
        <section>
          <h2 className="text-xs uppercase tracking-wider text-text-muted mb-2">{t('topics.available')}</h2>
          <div className="space-y-2">
            {available.map(tp => (
              <TopicRow key={tp.id} topic={tp} onClick={() => toggle(tp.id)} />
            ))}
          </div>
        </section>
        <p className="text-center text-[11px] text-text-muted pt-2">{t('topics.min_max_hint')}</p>
      </div>
    </div>
  );
}

function TopicRow({ topic, active, onClick }: { topic: Topic; active?: boolean; onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      onClick={onClick}
      className={[
        'w-full p-4 flex items-center gap-3 rounded-xl transition text-left',
        active ? 'bg-accent text-text-inverse' : 'bg-surface text-text hover:opacity-95',
      ].join(' ')}
    >
      <div className={`w-10 h-10 grid place-items-center rounded-md ${active ? 'bg-white/15' : 'bg-white/60'}`}>
        <TopicIcon id={topic.id} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate font-content">{topic.name}</div>
        <div className={`text-[11px] ${active ? 'opacity-80' : 'text-text-muted'}`}>
          {t('topics.count_line', { cards: topic.cards_count, books: topic.books_count })}
        </div>
      </div>
      <span className="text-base">{active ? '✓' : '+'}</span>
    </button>
  );
}
